/**
 * Global notification (toast) queue. Mirrors the design bundle's
 * `Toaster.jsx` + `window.showToast(...)` pattern in a Vue-idiomatic
 * way: state lives in a Pinia composition store, the UI surface is
 * `NotificationContainer.vue`, and call sites use the
 * `useNotifications()` composable.
 *
 * Items auto-dismiss after `duration ?? 4000` ms via a setTimeout
 * registered in `push`. Manual dismiss is supported through
 * `dismiss(id)` (the container wires this on click).
 */
import { ref } from 'vue'
import { defineStore, acceptHMRUpdate } from 'pinia'

export type NotificationKind = 'success' | 'error' | 'info' | 'warning'

export interface NotificationItem {
  id: string
  kind: NotificationKind
  title: string
  sub?: string
  /** Auto-dismiss timeout in ms. Defaults to 4000 inside `push`. */
  duration?: number
}

const DEFAULT_DURATION_MS = 4000

function makeId(): string {
  return `n-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

export const useNotificationsStore = defineStore('notifications', () => {
  const items = ref<NotificationItem[]>([])

  function push(item: Omit<NotificationItem, 'id'>): string {
    const id = makeId()
    const duration = item.duration ?? DEFAULT_DURATION_MS
    items.value.push({ ...item, id })
    if (duration > 0) {
      window.setTimeout(() => {
        dismiss(id)
      }, duration)
    }
    return id
  }

  function dismiss(id: string): void {
    items.value = items.value.filter((i) => i.id !== id)
  }

  return { items, push, dismiss }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useNotificationsStore, import.meta.hot))
}
